import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getAllBranchesThunk } from "../../../store/thunks/branchThunks";
import { setFilters, setCurrentPage } from "../../../store/slices/branchSlice";
import {
  StarIcon,
  OfficeBuildingIcon,
  LocationMarkerIcon,
  SwitchHorizontalIcon,
} from "@heroicons/react/solid";
import { useNavigate } from "react-router-dom";
import {
  BRANCH_TYPES,
  STATE_CODES,
  RATE_POLICIES,
} from "constants/branch.constants";

const BranchList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    allBranches,
    loadingList,
    filters,
    totalPages,
    currentPage,
    hybridPolicies,
  } = useSelector((state) => state.branch);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    dispatch(getAllBranchesThunk({ ...filters, page: currentPage }));
  }, [dispatch, filters, currentPage]);

  const getBranchTypeColor = (type) => {
    const colors = {
      FLAGSHIP: "bg-purple-100 text-purple-800",
      STANDARD: "bg-blue-100 text-blue-800",
      FRANCHISE: "bg-green-100 text-green-800",
      WAREHOUSE: "bg-gray-100 text-gray-800",
    };
    return colors[type] || "bg-gray-100 text-gray-800";
  };

  const getRatePolicyBadge = (branch) => {
    const policy = branch.rateConfig?.ratePolicy;

    if (policy === "HYBRID") {
      const hybridConfig =
        hybridPolicies[branch._id] || branch.rateConfig?.hybridConfig;
      if (hybridConfig) {
        const metalPolicies =
          hybridConfig.hybridConfig?.metalPolicies ||
          hybridConfig.metalPolicies ||
          {};
        const inheritCount = Object.values(metalPolicies).filter(
          (p) => p === "INHERIT"
        ).length;
        const customCount = Object.values(metalPolicies).filter(
          (p) => p === "CUSTOM"
        ).length;

        return (
          <div className="flex items-center gap-1">
            <span className="px-2 py-0.5 text-xs font-medium rounded bg-purple-100 text-purple-700">
              HYBRID
            </span>
            <span className="text-xs text-gray-500">
              ({inheritCount}I/{customCount}C)
            </span>
          </div>
        );
      }
      return (
        <span className="px-2 py-0.5 text-xs font-medium rounded bg-purple-100 text-purple-700">
          HYBRID
        </span>
      );
    }

    const colors = {
      INHERIT: "bg-blue-100 text-blue-700",
      CUSTOM: "bg-green-100 text-green-700",
    };

    return (
      <span
        className={`px-2 py-0.5 text-xs font-medium rounded ${
          colors[policy] || "bg-gray-100 text-gray-700"
        }`}
      >
        {RATE_POLICIES[policy] || "NOT SET"}
      </span>
    );
  };

  const handleViewDetails = (branchId) => {
    navigate(`/branches/${branchId}`);
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <input
            type="text"
            placeholder="Search branches..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />

          <select
            value={filters.state || ""}
            onChange={(e) => dispatch(setFilters({ state: e.target.value }))}
            className="px-4 py-2 border border-gray-300 rounded-lg"
          >
            <option value="">All States</option>
            {Object.entries(STATE_CODES).map(([code, name]) => (
              <option key={code} value={code}>
                {name}
              </option>
            ))}
          </select>

          <select
            value={filters.branchType || ""}
            onChange={(e) =>
              dispatch(setFilters({ branchType: e.target.value }))
            }
            className="px-4 py-2 border border-gray-300 rounded-lg"
          >
            <option value="">All Types</option>
            {Object.entries(BRANCH_TYPES).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>

          <select
            value={filters.ratePolicy || ""}
            onChange={(e) =>
              dispatch(setFilters({ ratePolicy: e.target.value }))
            }
            className="px-4 py-2 border border-gray-300 rounded-lg"
          >
            <option value="">All Policies</option>
            <option value="INHERIT">Inherit</option>
            <option value="CUSTOM">Custom</option>
            <option value="HYBRID">Hybrid</option>
          </select>

          <select
            value={filters.isActive}
            onChange={(e) =>
              dispatch(setFilters({ isActive: e.target.value === "true" }))
            }
            className="px-4 py-2 border border-gray-300 rounded-lg"
          >
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>
        </div>
      </div>

      {/* Branch Cards */}
      {loadingList ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div
              key={i}
              className="bg-white rounded-lg shadow-md p-6 animate-pulse"
            >
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
              <div className="h-3 bg-gray-200 rounded w-1/2"></div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {allBranches
            .filter(
              (branch) =>
                searchTerm === "" ||
                branch.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                branch.code.toLowerCase().includes(searchTerm.toLowerCase())
            )
            .map((branch) => (
              <div
                key={branch._id}
                className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => handleViewDetails(branch._id)}
              >
                <div className="p-6">
                  {/* Header */}
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="text-lg font-semibold">{branch.name}</h3>
                        {branch.isMainBranch && (
                          <StarIcon
                            className="h-5 w-5 text-yellow-500"
                            title="Main Branch"
                          />
                        )}
                      </div>
                      <p className="text-sm text-gray-500">{branch.code}</p>
                    </div>
                    <span
                      className={`px-2 py-1 text-xs font-semibold rounded-full ${getBranchTypeColor(
                        branch.branchType
                      )}`}
                    >
                      {branch.branchType}
                    </span>
                  </div>

                  {/* Location */}
                  <div className="flex items-start gap-2 mb-3">
                    <LocationMarkerIcon className="h-4 w-4 text-gray-400 mt-0.5" />
                    <div className="text-sm text-gray-600">
                      <p>
                        {branch.address.city}, {branch.address.state}
                      </p>
                      <p className="text-xs">{branch.address.pinCode}</p>
                    </div>
                  </div>

                  {/* Contact */}
                  <div className="text-sm text-gray-600 mb-4">
                    <p className="flex items-center gap-2">
                      <span className="font-medium">Phone:</span>{" "}
                      {branch.contact.phoneNumber}
                    </p>
                    {branch.contact.email && (
                      <p className="flex items-center gap-2 text-xs">
                        <span className="font-medium">Email:</span>{" "}
                        {branch.contact.email}
                      </p>
                    )}
                  </div>

                  {/* Rate Config */}
                  <div className="pt-3 border-t space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500">Policy:</span>
                        {getRatePolicyBadge(branch)}
                      </div>

                      {branch.hasStores && (
                        <div className="flex items-center gap-1">
                          <OfficeBuildingIcon className="h-4 w-4 text-gray-400" />
                          <span className="text-xs text-gray-500">
                            {branch.storeCount} stores
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500">Rate Mode:</span>
                      <span
                        className={`px-2 py-0.5 text-xs font-medium rounded ${
                          branch.rateConfig?.rateMode === "LIVE"
                            ? "bg-green-100 text-green-700"
                            : branch.rateConfig?.rateMode === "ADMIN"
                            ? "bg-blue-100 text-blue-700"
                            : "bg-yellow-100 text-yellow-700"
                        }`}
                      >
                        {branch.rateConfig?.rateMode || "ADMIN"}
                      </span>
                    </div>

                    {/* Hybrid Policy Details for HYBRID branches */}
                    {branch.rateConfig?.ratePolicy === "HYBRID" && (
                      <div className="mt-2 pt-2 border-t">
                        <div className="flex items-center gap-1 text-xs text-purple-600">
                          <SwitchHorizontalIcon className="h-3 w-3" />
                          <span>Mixed inheritance configuration</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2 mt-6">
          {[...Array(totalPages)].map((_, i) => (
            <button
              key={i}
              onClick={() => dispatch(setCurrentPage(i + 1))}
              className={`px-3 py-1 rounded ${
                currentPage === i + 1
                  ? "bg-blue-600 text-white"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300"
              }`}
            >
              {i + 1}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default BranchList;
